/**
 * The rows behind the little info tooltip on a location header in the operational view.
 *
 * These used to be a full-height "Informacoes" card eating a third of the frame, mostly to repeat
 * what the breadcrumb and the tab counters already said. As a tooltip they cost nothing until
 * someone actually wants them.
 */

import { departmentFieldDisplayRows } from '../../../utils/departmentFields';
import type { CustomDepartmentField } from '../../../types/customer/base';

export interface LocationInfoRow {
  label: string;
  value: string;
}

export interface BuildLocationInfoRowsInput {
  /**
   * The extra fields this empresa declared its departamentos carry (CNPJ, nome fantasia, …).
   * Only meaningful for a departamento; a unidade and a bloco have none.
   */
  departmentFields?: CustomDepartmentField[];
  locationType: 'unit' | 'block' | 'department' | string;
  locationInfo: any;
  peopleCount: number;
  unitsCount: number;
  /** "Moradores" or "Funcionarios", per customer type. */
  peopleLabel: string;
  /** "Unidade" / "Casa" / "Apartamento", per condo type. */
  unitLabel: string;
  formatDate: (value?: string) => string;
}

export function buildLocationInfoRows({
  locationType,
  locationInfo,
  peopleCount,
  unitsCount,
  peopleLabel,
  unitLabel,
  formatDate,
  departmentFields,
}: BuildLocationInfoRowsInput): LocationInfoRow[] {
  const info = locationInfo || {};
  const rows: LocationInfoRow[] = [];

  if (info.description) rows.push({ label: 'Descrição', value: String(info.description) });
  if (info.manager_name) rows.push({ label: 'Responsável', value: String(info.manager_name) });
  // A bloco is not inside a bloco, and a departamento has no bloco.
  if (info.block_name && locationType === 'unit') rows.push({ label: 'Bloco', value: String(info.block_name) });
  if (info.street_name && locationType === 'unit') {
    rows.push({
      label: 'Endereço',
      value: info.street_number ? `${info.street_name}, ${info.street_number}` : String(info.street_name),
    });
  }

  rows.push(
    locationType === 'block'
      ? { label: `Total de ${unitLabel.toLowerCase()}s`, value: String(unitsCount) }
      : { label: `Total de ${peopleLabel.toLowerCase()}`, value: String(peopleCount) },
  );

  // A departamento's declared fields. The porteiro works in this frame, and sending them to the
  // management page to read a CNPJ is the same as not having it. Unfilled ones still appear —
  // an absent row is indistinguishable from a field the empresa never declared.
  if (locationType === 'department') {
    departmentFieldDisplayRows(departmentFields, info.custom_fields, { includeEmpty: true })
      .forEach(row => rows.push({ label: row.label, value: row.value }));
  }

  rows.push({ label: 'Criado em', value: formatDate(info.created_at) });
  rows.push({ label: 'Atualizado em', value: formatDate(info.updated_at) });

  return rows;
}
